import { Fragment, useMemo } from 'react';
import { useLocation } from '@tanstack/react-router';
import WithAccessControl from 'src/shared/components/utils/WithAccessControl';
import NavigationTab from 'src/shared/components/ui/navigation/tabs-navigation';
import PageMain from 'src/shared/components/layout/PageMain';
import PageBody from 'src/shared/components/layout/PageBody';
import { ReactChildren } from 'src/shared/types/util.types';
import { Role } from 'src/shared/types/organisation.types';
import { Icons } from 'src/shared/components/ui/icons/dynamic-icon';
import PageHeaderBar from 'src/shared/components/layout/PageHeaderBar';
import { FeatureFlag } from 'src/shared/constants/featureFlags.constant';
import { useBreadcrumbLevel } from 'src/shared/hooks/useBreadcrumbLevel';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { FEATURE_FLAGS } from 'src/shared/constants/featureFlags.constant';

interface NavigationItem {
  title: string;
  href: string;
  icon: Icons;
  allowedRoles?: Role[];
  supportOnly: boolean;
  featureFlag?: FeatureFlag;
  testId?: string;
  badge?: string;
}

const navigationItems: NavigationItem[] = [
  {
    title: 'Automation Flows',
    href: '/flows',
    icon: 'Workflow' as Icons,
    allowedRoles: undefined,
    supportOnly: false,
  },
  {
    title: 'Templates',
    href: '/flows/templates',
    icon: 'Files' as Icons,
    allowedRoles: undefined,
    supportOnly: false,
  },
  {
    title: 'Custom Actions',
    href: '/flows/code',
    icon: 'TekstAppIcon' as Icons,
    allowedRoles: ['ADMIN', 'SALES'] as Role[],
    supportOnly: false,
  },
];

export function FlowsNav({ children }: ReactChildren) {
  const { pathname } = useLocation();
  const { role, isSupport, featureFlags } = useOrg();

  const selectedItem = useMemo(() => {
    if (pathname === '/flows') {
      return navigationItems[0];
    }

    if (pathname === '/flows/templates') {
      return navigationItems[1];
    }

    if (pathname.includes('/flows/code')) {
      return navigationItems[2];
    }

    return navigationItems[0];
  }, [pathname]);

  useBreadcrumbLevel(1, 'Flows', '/flows');

  const visibleItems = useMemo(() => {
    return navigationItems.filter((item) => {
      if (item.featureFlag && !featureFlags?.has(FEATURE_FLAGS[item.featureFlag as FeatureFlag])) {
        return false;
      }
      if (item.allowedRoles && item.allowedRoles.length > 0) {
        return isSupport || item.allowedRoles.includes(role as Role);
      }
      if (item.supportOnly) {
        return isSupport;
      }
      return true;
    });
  }, [featureFlags, isSupport, role]);

  return (
    <PageMain excludeMaxWidth>
      <PageHeaderBar noBorder />
      <div className="max-w-full px-5 border-b flex" style={{ gridArea: 'header' }}>
        <div className="w-fit translate-y-[1px] flex items-center gap-3">
          {visibleItems.map((item) => {
            const element = (
              <NavigationTab
                key={item.href}
                testId={item.testId}
                icon={item.icon}
                href={item.href}
                text={item.title}
                isActive={item.href === selectedItem?.href}
                badge={item.badge}
              />
            );

            const needsAccessControl = !!(item.supportOnly || (item.allowedRoles && item.allowedRoles.length > 0));
            if (needsAccessControl) {
              return (
                <WithAccessControl key={item.href} allowedRoles={item.allowedRoles as Role[] | undefined}>
                  {element}
                </WithAccessControl>
              );
            }

            return <Fragment key={item.href}>{element}</Fragment>;
          })}
        </div>
      </div>

      <PageBody allowScroll={false}>{children}</PageBody>
    </PageMain>
  );
}
